import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, HomeIcon, CalculatorIcon, CurrencyEuroIcon, ChartBarIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { LightBulbIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { authFetch } from '../utils/authFetch';
import PlaceholderImage from '../components/PlaceholderImage';
import AdvancedInvestmentIndicators from '../components/AdvancedInvestmentIndicators';
import EstadoGeneralThermometer from '../components/EstadoGeneralThermometer';
import PropertyReformDetails from '../components/PropertyReformDetails';
import BotonAnalizarIA from '../components/BotonAnalizarIA';
import Spinner from '../components/Spinner';
import InvestmentLoader from '../components/InvestmentLoader';

const PropertyAnalysis = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [analysisData, setAnalysisData] = useState(null);
    const [activeTab, setActiveTab] = useState('descripcion'); // precio, reforma, mercado
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [images, setImages] = useState([]);
    const [investmentIndicators, setInvestmentIndicators] = useState(null);
    const [loadingIndicators, setLoadingIndicators] = useState(false);
    const [loadingRentabilidadTab, setLoadingRentabilidadTab] = useState(false);
    const [iaCheck, setIaCheck] = useState(null);
    const [iaSummary, setIaSummary] = useState(null);
    const [loadingIa, setLoadingIa] = useState(false);
    const [resumenIA, setResumenIA] = useState(null);
    const [investmentIndicatorsIA, setInvestmentIndicatorsIA] = useState(null);
    const [originalReformas, setOriginalReformas] = useState(null);
    const [costesVacacional, setCostesVacacional] = useState(null);
    const [modoSoloIA, setModoSoloIA] = useState(false);

    useEffect(() => {
        const fetchPropertyData = async () => {
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${id}`, {
                    credentials: "include"
                });
                if (!res.ok) throw new Error('Error fetching property');
                const data = await res.json();
                setProperty(data);

                // Fetch property images
                const imagesRes = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${id}/imagenes`, {
                    credentials: "include"
                });
                if (!imagesRes.ok) throw new Error('Error fetching images');
                const imagesData = await imagesRes.json();
                setImages(imagesData);

                // Fetch price analysis data
                const analysisRes = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${id}/analisis-precio`, {
                    credentials: "include"
                });
                if (!analysisRes.ok) throw new Error('Error fetching price analysis');
                const analysisData = await analysisRes.json();
                setAnalysisData(analysisData);
            } catch (err) {
                console.error("Error fetching data:", err);
                toast.error("Error al cargar los datos");
            } finally {
                setLoading(false);
            }
        };

        fetchPropertyData();
    }, [id]);

    // Save original reformas on first property load
    useEffect(() => {
        if (property && !originalReformas) {
            setOriginalReformas(property.reformas);
        }
    }, [property, originalReformas]);

    const nextImage = () => {
        setCurrentImageIndex((prevIndex) => 
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = () => {
        setCurrentImageIndex((prevIndex) => 
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    const fetchAllAdvancedIndicators = async () => {
        setLoadingIndicators(true);
        try {
            const data = await authFetch(`${window.REACT_APP_API_ENDPOINT}/indicadores/calcular-indicadores-avanzados/${id}`);
            setInvestmentIndicators(data);
        } catch (err) {
            toast.error('Error al cargar los indicadores de rentabilidad');
        } finally {
            setLoadingIndicators(false);
            setLoadingRentabilidadTab(false);
        }
    };

    const fetchIAAdvancedIndicators = async () => {
        setLoadingIndicators(true);
        try {
            const data = await authFetch(`${window.REACT_APP_API_ENDPOINT}/indicadores/calcular-indicadores-avanzados/${id}?soloIA=true`);
            setInvestmentIndicatorsIA(data);
        } catch (err) {
            toast.error('Error al cargar los indicadores IA de rentabilidad');
        } finally {
            setLoadingIndicators(false);
        }
    };

    const mergeIAWithOriginalData = (iaData, originalData) => {
        if (!iaData || !originalData) return originalData;

        const mergeList = (originalList, iaList, type) => {
            return originalList.map(originalItem => {
                const isPublicTemplate = originalItem.tipoReforma?.nombre === "Base Reforma Estándar" || 
                                         originalItem.tipoReforma?.nombre === "Base Reforma Premium";
                if (isPublicTemplate) {
                    // Determine which IA type to match
                    const isEstandar = originalItem.tipoReforma?.nombre === "Base Reforma Estándar";
                    const iaItem = iaList?.find(ia =>
                        ia.constructor === originalItem.constructor &&
                        (
                            (isEstandar && ia.tipoReforma?.nombre.includes("Estándar IA")) ||
                            (!isEstandar && ia.tipoReforma?.nombre.includes("Premium IA"))
                        )
                    );
                    if (iaItem) {
                        return {
                            ...iaItem,
                            tipoReforma: {
                                ...iaItem.tipoReforma,
                                nombre: originalItem.tipoReforma.nombre // Preserve original name for sorting and display
                            },
                            originalData: {
                                costeReforma: originalItem.costeReforma,
                                venta: originalItem.venta,
                                alquiler: originalItem.alquiler
                            }
                        };
                    }
                }
                // Return original item unchanged (private templates or no IA match)
                return originalItem;
            });
        };

        const mergedData = {
            ...originalData,
            venta: mergeList(originalData.venta, iaData.venta, 'venta'),
            alquiler: mergeList(originalData.alquiler, iaData.alquiler, 'alquiler'),
        };

        // Reapply sorting criteria after merging
        const reapplySorting = (list) => {
            if (!list || !Array.isArray(list)) return list;
            
            // Import sorting functions from helper
            const SCENARIO_ORDER = ["Sin reforma", "Base reformas", "Custom reformas"];
            
            const getScenarioCategory = (name) => {
                if (name.toLowerCase().includes('sin reforma')) return "Sin reforma";
                if (name.toLowerCase().includes('base reforma')) return "Base reformas";
                return "Custom reformas"; // All other reformas are custom
            };
            
            const getScenarioSubCategory = (name) => {
                if (name.toLowerCase().includes('sin reforma')) return 0;
                if (name.toLowerCase().includes('base reforma estándar')) return 1;
                if (name.toLowerCase().includes('base reforma premium')) return 2;
                // For custom reformas, sort by cost (will be handled in the main sorting)
                return 3;
            };
            
            const getConstructorOrder = (constructor) => {
                if (constructor === 'Sin constructor') return 0;
                if (constructor === 'Genérico calidad estándar') return 1;
                return 2; // Custom constructors
            };

            return list.sort((a, b) => {
                // First sort by scenario category
                const categoryA = getScenarioCategory(a.tipoReforma?.nombre || '');
                const categoryB = getScenarioCategory(b.tipoReforma?.nombre || '');
                const orderA = SCENARIO_ORDER.indexOf(categoryA);
                const orderB = SCENARIO_ORDER.indexOf(categoryB);
                
                if (orderA !== orderB) return orderA - orderB;
                
                // Within same category, sort by sub-category for base reformas
                if (categoryA === "Base reformas" && categoryB === "Base reformas") {
                    const subOrderA = getScenarioSubCategory(a.tipoReforma?.nombre || '');
                    const subOrderB = getScenarioSubCategory(b.tipoReforma?.nombre || '');
                    if (subOrderA !== subOrderB) return subOrderA - subOrderB;
                }
                
                // Within same category/sub-category, sort by cost (lowest first)
                const costA = parseFloat(a.costeReforma) || 0;
                const costB = parseFloat(b.costeReforma) || 0;
                if (costA !== costB) return costA - costB;
                
                // If costs are equal, sort by constructor
                const constructorA = a.constructor || 'Sin constructor';
                const constructorB = b.constructor || 'Sin constructor';
                const constructorOrderA = getConstructorOrder(constructorA);
                const constructorOrderB = getConstructorOrder(constructorB);
                
                if (constructorOrderA !== constructorOrderB) return constructorOrderA - constructorOrderB;
                
                // If both are custom constructors, sort alphabetically
                if (constructorOrderA === 2 && constructorOrderB === 2) {
                    return constructorA.localeCompare(constructorB);
                }
                
                return 0;
            });
        };

        // Apply sorting to both venta and alquiler lists
        mergedData.venta = reapplySorting(mergedData.venta);
        mergedData.alquiler = reapplySorting(mergedData.alquiler);

        // Debug: log the final sorted order
        console.log('[DEBUG] mergeIAWithOriginalData - Final sorted venta order:', 
            mergedData.venta?.map(item => ({nombre: item.tipoReforma?.nombre, constructor: item.constructor, coste: item.costeReforma})));
        console.log('[DEBUG] mergeIAWithOriginalData - Final sorted alquiler order:', 
            mergedData.alquiler?.map(item => ({nombre: item.tipoReforma?.nombre, constructor: item.constructor, coste: item.costeReforma})));

        return mergedData;
    };

    const handleRentabilidadTabClick = () => {
        setActiveTab('rentabilidad');
        if (!investmentIndicators) {
            setLoadingRentabilidadTab(true);
            fetchAllAdvancedIndicators();
        }
    };

    // Fetch normal investment indicators when rentabilidad tab is opened
    useEffect(() => {
        if (activeTab === 'rentabilidad' && !investmentIndicators && !loadingRentabilidadTab) {
            setLoadingRentabilidadTab(true);
            fetchAllAdvancedIndicators();
        }
    }, [activeTab, id, investmentIndicators, loadingRentabilidadTab]);

    // Fetch IA investment indicators when rentabilidad tab is opened or toggled ON
    useEffect(() => {
        if (activeTab === 'rentabilidad' && !investmentIndicatorsIA) {
            setLoadingIndicators(true);
            fetchIAAdvancedIndicators();
        }
    }, [activeTab, id, investmentIndicatorsIA]);

    // Fetch IA analysis when the tab is selected
    useEffect(() => {
        if (activeTab === 'inteligente' && iaCheck === null) {
            setLoadingIa(true);
            fetch(`${window.REACT_APP_API_ENDPOINT}/api/reformas-ia/propiedad/${id}/check-reformas-ia`, { credentials: 'include' })
                .then(res => res.json())
                .then(data => {
                    setIaCheck(data);
                    if (data?.existen) {
                        fetch(`${window.REACT_APP_API_ENDPOINT}/api/reformas-ia/propiedad/${id}/obtener-resumen-ia`, { credentials: 'include' })
                            .then(res => res.json())
                            .then(setIaSummary)
                            .catch(() => toast.error('Error al cargar el resumen IA'))
                            .finally(() => setLoadingIa(false));
                    } else {
                        setLoadingIa(false);
                    }
                })
                .catch(() => {
                    toast.error('Error al comprobar el análisis IA');
                    setLoadingIa(false);
                });
        }
    }, [activeTab, id, iaCheck]);

    // Fetch resumenIA only when opening 'reforma' or 'rentabilidad' tab and if not already loaded
    useEffect(() => {
        if ((activeTab === 'reforma' || activeTab === 'rentabilidad') && !resumenIA) {
            fetch(`${window.REACT_APP_API_ENDPOINT}/api/reformas-ia/propiedad/${id}/obtener-resumen-ia`, {
                credentials: 'include'
            })
                .then(res => res.json())
                .then(data => setResumenIA(data))
                .catch(err => console.error('Error al obtener resumen IA:', err));
        }
    }, [activeTab, resumenIA, id]);

    const handleCalcularVacacional = async () => {
        setLoadingIndicators(true);
        try {
            await fetchAllAdvancedIndicators();
        } catch (error) {
            console.error("Error calculating vacation rental indicators:", error);
        } finally {
            setLoadingIndicators(false);
        }
    };

    const cargarCostesVacacional = async () => {
        console.log('🔍 PropertyAnalysis - cargarCostesVacacional called');
        try {
            console.log('🔍 PropertyAnalysis - Making API call to alquiler-vacacional');
            const response = await fetch(`${window.REACT_APP_API_ENDPOINT}/alquiler-vacacional/${id}`, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('🔍 PropertyAnalysis - API response:', data);
            
            if (data) {
                console.log('🔍 PropertyAnalysis - Costes data loaded successfully');
                setCostesVacacional(data);
                return data;
            } else {
                console.log('❌ PropertyAnalysis - No data received from API');
                return null;
            }
        } catch (error) {
            console.error("❌ PropertyAnalysis - Error loading vacation rental costs:", error);
            throw error;
        }
    };

    const cargarReformasConIA = async () => {
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/reformas-ia/propiedad/${id}/obtener-reformas-sugerencias-ia`, {
                credentials: "include",
            });
            const data = await res.json();
            if (data && property) {
                setProperty(prev => ({
                    ...prev,
                    reformas: data
                }));
            }
        } catch (err) {
            console.error("Error cargando reformas con sugerencias IA:", err);
        }
    };

    const restaurarReformasOriginales = () => {
        if (originalReformas && property) {
            setProperty(prev => ({
                ...prev,
                reformas: originalReformas
            }));
        }
    };

    const handleExportPdf = (propertyId) => {
        fetch(`/api/properties/${propertyId}/export-pdf`, {
            method: 'GET',
            headers: { 'Accept': 'application/pdf' },
        })
            .then(response => response.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `property_${propertyId}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
                        <div className="h-96 bg-gray-200 rounded mb-8"></div>
                        <div className="h-64 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!property) {
        return (
            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h2 className="text-2xl font-semibold text-gray-900">Propiedad no encontrada</h2>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeftIcon className="h-5 w-5 mr-2" />
                        Volver
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900 mt-4">{property.titulo}</h1>
                    <p className="text-gray-600 mt-2">{property.municipio}, {property.distrito}</p>
                </div>

                {/* Export to PDF Button */}
                <button
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mb-4"
                    onClick={() => handleExportPdf(property.id)}
                >
                    Exportar a PDF
                </button>

                {/* Property Details */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
                    <div className="relative h-96">
                        {images.length > 0 ? (
                            <>
                                <img
                                    src={images[currentImageIndex].url_imagen}
                                    alt={`${property.titulo} - Imagen ${currentImageIndex + 1}`}
                                    className="w-full h-full object-cover"
                                />
                                {images.length > 1 && (
                                    <>
                                        <button
                                            onClick={prevImage}
                                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                                        >
                                            <ChevronLeftIcon className="h-6 w-6" />
                                        </button>
                                        <button
                                            onClick={nextImage}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                                        >
                                            <ChevronRightIcon className="h-6 w-6" />
                                        </button>
                                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                                            {images.map((_, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentImageIndex(index)}
                                                    className={`w-2 h-2 rounded-full ${
                                                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="w-full h-full">
                                <PlaceholderImage />
                            </div>
                        )}
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <span className="text-gray-600">Precio</span>
                                <p className="text-xl font-semibold">{property.precio}€</p>
                            </div>
                            <div>
                                <span className="text-gray-600">Superficie</span>
                                <p className="text-xl font-semibold">{property.superficie}m²</p>
                            </div>
                            <div>
                                <span className="text-gray-600">Habitaciones</span>
                                <p className="text-xl font-semibold">{property.habitaciones}</p>
                            </div>
                            <div>
                                <span className="text-gray-600">Baños</span>
                                <p className="text-xl font-semibold">{property.banos}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Analysis Tabs */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            <button
                                onClick={() => setActiveTab('descripcion')}
                                className={`flex items-center px-6 py-4 text-sm font-medium ${
                                    activeTab === 'descripcion'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <DocumentTextIcon className="h-5 w-5 mr-2" />
                                Descripción
                            </button>
                            <button
                                onClick={() => setActiveTab('precio')}
                                className={`flex items-center px-6 py-4 text-sm font-medium ${
                                    activeTab === 'precio'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <CurrencyEuroIcon className="h-5 w-5 mr-2" />
                                Análisis de Precio
                            </button>
                            <button
                                onClick={() => setActiveTab('reforma')}
                                className={`flex items-center px-6 py-4 text-sm font-medium ${
                                    activeTab === 'reforma'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <CalculatorIcon className="h-5 w-5 mr-2" />
                                Reforma
                            </button>
                            <button
                                onClick={handleRentabilidadTabClick}
                                className={`flex items-center px-6 py-4 text-sm font-medium ${
                                    activeTab === 'rentabilidad'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                                disabled={loadingRentabilidadTab}
                            >
                                {loadingRentabilidadTab ? (
                                    <>
                                        <Spinner size="sm" color="blue" className="mr-2" />
                                        Cargando...
                                    </>
                                ) : (
                                    <>
                                        <ChartBarIcon className="h-5 w-5 mr-2" />
                                        Análisis de Rentabilidad
                                    </>
                                )}
                            </button>
                            <button
                                className={`flex items-center px-6 py-4 text-sm font-medium ${
                                    activeTab === 'inteligente'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                                onClick={() => setActiveTab('inteligente')}
                            >
                                <LightBulbIcon className="h-5 w-5 mr-2" />
                                Análisis Inteligente
                            </button>
                        </nav>
                    </div>

                    <div className="p-6">
                        {activeTab === 'descripcion' && (
                            <div className="bg-white border border-gray-200 rounded-lg shadow p-8 mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">{property.titulo}</h2>
                                <p className="text-gray-500 mb-2">
                                    <span className="font-medium">Dirección:</span> {property.direccion || 'No disponible'}
                                </p>
                                <p className="text-gray-700 text-base whitespace-pre-line mb-2">{property.descripcion || 'Sin descripción.'}</p>
                            </div>
                        )}

                        {activeTab === 'precio' && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold">Análisis de Precio</h3>
                                <div className="grid grid-cols-1 gap-6">
                                    {/* Precios de Venta */}
                                    <div className="bg-white p-6 rounded-lg border">
                                        <h4 className="text-lg font-medium mb-4">Precios de Venta</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Precio de la propiedad */}
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <span className="text-gray-600">Precio por m² de tu propiedad</span>
                                                <div className="flex items-center justify-between">
                                                    <p className="text-xl font-semibold">
                                                        {Math.round(property.precio / property.superficie)}€/m²
                                                    </p>
                                                    {analysisData?.tieneDatosZona && analysisData.preciosZona?.venta && (
                                                        <span className={`text-sm ${
                                                            (property.precio / property.superficie) > analysisData.preciosZona.venta[0].precio_m2 
                                                                ? 'text-red-600' 
                                                                : 'text-green-600'
                                                        }`}>
                                                            {Math.abs(((property.precio / property.superficie - analysisData.preciosZona.venta[0].precio_m2) / analysisData.preciosZona.venta[0].precio_m2 * 100)).toFixed(1)}% 
                                                            {(property.precio / property.superficie) > analysisData.preciosZona.venta[0].precio_m2 ? ' por encima ' : ' por debajo '} 
                                                            de la media
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Precios medios de venta */}
                                            {analysisData?.tieneDatosZona && analysisData.preciosZona?.venta && (
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <span className="text-gray-600">Precios medios de venta en la zona</span>
                                                    {analysisData.preciosZona.venta.map((precio, index) => (
                                                        <div key={index} className="mt-2">
                                                            <p className="text-xl font-semibold">
                                                                {Math.round(precio.precio_m2)}€/m²
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Precios de Alquiler */}
                                    {analysisData?.tieneDatosZona && (analysisData.preciosZona?.alquiler || analysisData.preciosZona?.['Alquiler Vacacional']) && (
                                        <div className="bg-white p-6 rounded-lg border">
                                            <h4 className="text-lg font-medium mb-4">Precios de Alquiler</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                {/* Precios medios de alquiler */}
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <span className="text-gray-600">Precios medios de alquiler en la zona</span>
                                                    {analysisData.preciosZona.alquiler?.map((precio, index) => (
                                                        <div key={index} className="mt-2">
                                                            <p className="text-xl font-semibold">
                                                                {Math.round(precio.precio_m2)}€/m²
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Estimación de alquiler mensual */}
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <span className="text-gray-600">Estimación de alquiler mensual</span>
                                                    {analysisData.preciosZona.alquiler?.map((precio, index) => (
                                                        <div key={index} className="mt-2">
                                                            <p className="text-xl font-semibold">
                                                                {Math.round(precio.precio_m2 * property.superficie)}€/mes
                                                            </p>
                                                            <p className="text-sm text-gray-500">
                                                                Basado en {Math.round(precio.precio_m2)}€/m²
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Estimación de alquiler vacacional */}
                                                <div className="bg-gray-50 p-4 rounded-lg">
                                                    <span className="text-gray-600">Estimación de alquiler vacacional mensual</span>
                                                    {analysisData.preciosZona['Alquiler Vacacional']?.map((precio, index) => (
                                                        <div key={index} className="mt-2">
                                                            <p className="text-xl font-semibold">
                                                                {Math.round(precio.precio_m2 * property.superficie)}€/mes
                                                            </p>
                                                            <p className="text-sm text-gray-500">
                                                                Basado en {Math.round(precio.precio_m2)}€/m²
                                                            </p>
                                                        </div>
                                                    ))}
                                                    {(!analysisData.preciosZona['Alquiler Vacacional'] || analysisData.preciosZona['Alquiler Vacacional'].length === 0) && (
                                                        <div className="mt-2">
                                                            <p className="text-sm text-gray-500 italic">
                                                                Datos no disponibles para esta zona
                                                            </p>
                                                            <p className="text-xs text-gray-400 mt-1">
                                                                El mercado de alquiler vacacional requiere datos específicos de precios por zona
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'reforma' && (
                            <div className="py-6">
                                {/* IA Switch for Reforma Tab */}
                                {resumenIA && !resumenIA.error && (
                                    <div className="mb-6 bg-blue-100 p-4 rounded-lg shadow-md border border-blue-200">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <h3 className="text-lg font-semibold text-blue-800 mb-2">Análisis Inteligente</h3>
                                                <p className="text-sm text-blue-700">
                                                    Activa el switch para ver las sugerencias de reforma optimizadas por IA
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-center space-y-1">
                                                <span className="text-xs font-semibold text-gray-700">
                                                    {modoSoloIA ? "IA ON" : "IA OFF"}
                                                </span>
                                                <label className="inline-flex relative items-center cursor-pointer scale-110">
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only peer"
                                                        checked={modoSoloIA}
                                                        onChange={(e) => {
                                                            setModoSoloIA(e.target.checked);
                                                            if (e.target.checked) {
                                                                cargarReformasConIA();
                                                            } else {
                                                                restaurarReformasOriginales();
                                                            }
                                                        }}
                                                    />
                                                    <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-400 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 relative">
                                                        <span className="absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 ease-in-out peer-checked:translate-x-6 flex items-center justify-center text-yellow-400 text-lg">
                                                            {modoSoloIA ? "💡" : ""}
                                                        </span>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {property?.reformas && property.reformas.length > 0 ? (
                                    <PropertyReformDetails
                                        propiedad={property}
                                        indicadores={[]}
                                        modoComparacion={false}
                                        setModoComparacion={() => {}}
                                        soloIA={modoSoloIA}
                                        setSoloIA={(activo) => {
                                            setModoSoloIA(activo);
                                            if (activo) {
                                                cargarReformasConIA();
                                            } else {
                                                restaurarReformasOriginales();
                                            }
                                        }}
                                        existenReformasIA={resumenIA !== null && !resumenIA?.error}
                                    />
                                ) : (
                                    <div className="flex flex-col items-center justify-center gap-4">
                                        <button
                                            className="px-6 py-3 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition disabled:bg-blue-300"
                                            onClick={async () => {
                                                setLoading(true);
                                                try {
                                                    const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/calcular-reforma/${property.id}`, {
                                                        method: 'POST',
                                                        credentials: 'include'
                                                    });
                                                    if (!res.ok) throw new Error('Error al calcular la reforma');
                                                    toast.success('Reforma calculada correctamente');
                                                    // Reload property data
                                                    const propRes = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${property.id}`, { credentials: 'include' });
                                                    const propData = await propRes.json();
                                                    setProperty(propData);
                                                } catch (err) {
                                                    toast.error('No se pudo calcular la reforma');
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                            disabled={loading}
                                        >
                                            {loading ? 'Calculando...' : 'Calcular Reforma'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'rentabilidad' && (
                            <div className="py-6">
                                {/* IA Switch for Rentabilidad Tab */}
                                {resumenIA && !resumenIA.error && (
                                    <div className="mb-6 bg-blue-100 p-4 rounded-lg shadow-md border border-blue-200">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <h3 className="text-lg font-semibold text-blue-800 mb-2">Análisis Inteligente</h3>
                                                <p className="text-sm text-blue-700">
                                                    Activa el switch para ver los indicadores de inversión optimizados por IA
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-center space-y-1">
                                                <span className="text-xs font-semibold text-gray-700">
                                                    {modoSoloIA ? "IA ON" : "IA OFF"}
                                                </span>
                                                <label className="inline-flex relative items-center cursor-pointer scale-110">
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only peer"
                                                        checked={modoSoloIA}
                                                        onChange={(e) => {
                                                            setModoSoloIA(e.target.checked);
                                                            if (e.target.checked) {
                                                                // Load IA investment indicators if not already loaded
                                                                if (!investmentIndicatorsIA) {
                                                                    fetchIAAdvancedIndicators();
                                                                }
                                                            } else {
                                                                restaurarReformasOriginales();
                                                            }
                                                        }}
                                                    />
                                                    <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-400 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 relative">
                                                        <span className="absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 ease-in-out peer-checked:translate-x-6 flex items-center justify-center text-yellow-400 text-lg">
                                                            {modoSoloIA ? "💡" : ""}
                                                        </span>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {loadingRentabilidadTab ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <InvestmentLoader />
                                    </div>
                                ) : (
                                    <AdvancedInvestmentIndicators
                                        indicadores={modoSoloIA ? mergeIAWithOriginalData(investmentIndicatorsIA, investmentIndicators) : investmentIndicators}
                                        indicadoresOriginales={modoSoloIA ? investmentIndicators : null}
                                        vacacional={investmentIndicators?.['Alquiler Vacacional']}
                                        loadingVacacional={loadingIndicators}
                                        onCalcularVacacional={handleCalcularVacacional}
                                        onShowDetails={() => {}}
                                        costesVacacional={costesVacacional}
                                        onCargarCostesVacacional={cargarCostesVacacional}
                                        modoIA={modoSoloIA}
                                        preserveOrder={modoSoloIA}
                                    />
                                )}
                            </div>
                        )}

                        {activeTab === 'inteligente' && (
                            <div className="py-6">
                                {loadingIa ? (
                                    <div className="text-center text-blue-600">Cargando análisis inteligente...</div>
                                ) : iaCheck && !iaCheck.existen ? (
                                    <div className="text-center text-gray-500 flex flex-col items-center gap-4">
                                        <span>No hay análisis inteligente disponible para esta propiedad.</span>
                                        {images.length >= 4 ? (
                                            <button
                                                className={`px-6 py-2 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 transition disabled:bg-blue-300`}
                                                disabled={false}
                                                onClick={async () => {
                                                    try {
                                                        const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/reformas-ia/propiedad/${id}/analisis-ia`, {
                                                            method: 'POST',
                                                            credentials: 'include'
                                                        });
                                                        if (!res.ok) throw new Error('Error al lanzar el análisis IA');
                                                        toast.success('Análisis IA lanzado. Esto puede tardar unos minutos.');
                                                        // Re-check IA status after a short delay
                                                        setTimeout(() => setIaCheck(null), 2000);
                                                    } catch (err) {
                                                        toast.error('No se pudo lanzar el análisis IA');
                                                    }
                                                }}
                                            >
                                                Generar análisis inteligente
                                            </button>
                                        ) : (
                                            <div className="text-red-600 font-semibold">⚠️ Debes subir al menos 4 imágenes para poder analizar por IA.</div>
                                        )}
                                    </div>
                                ) : iaSummary ? (
                                    <div className="bg-white border border-gray-200 rounded-lg shadow p-8 mb-8">
                                        <h2 className="text-2xl font-bold text-blue-800 mb-6">Análisis Inteligente de la Propiedad</h2>
                                        <EstadoGeneralThermometer estadoGeneral={iaSummary.estadoGeneral} />
                                        <div className="mb-8">
                                            <span className="font-semibold text-gray-700">Valoración del estado:</span>
                                            <p className="text-gray-800 mt-1">{iaSummary.valoracion_estado || '-'}</p>
                                        </div>
                                        <hr className="my-6" />
                                        <div className="mb-8">
                                            <span className="font-semibold text-gray-700">Dificultad y tiempo estimado:</span>
                                            <p className="text-gray-800 mt-1">{iaSummary.dificultad_tiempo || '-'}</p>
                                        </div>
                                        <hr className="my-6" />
                                        <div>
                                            <span className="font-semibold text-gray-700">Potencial de mejora:</span>
                                            <p className="text-gray-800 mt-1">{iaSummary.potencial_mejora || '-'}</p>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Floating IA Switch */}
            {resumenIA && !resumenIA.error && (
                <div className="fixed bottom-6 right-6 z-50 bg-white shadow-lg rounded-full px-4 py-2 flex items-center space-x-2 border border-blue-300">
                    <span className={`text-sm font-semibold ${modoSoloIA ? "text-blue-700" : "text-gray-500"}`}>
                        {modoSoloIA ? "IA ON" : "IA OFF"}
                    </span>
                    <label className="inline-flex relative items-center cursor-pointer scale-110">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={modoSoloIA}
                            onChange={(e) => {
                                setModoSoloIA(e.target.checked);
                                if (e.target.checked) {
                                    // Load appropriate data based on active tab
                                    if (activeTab === 'reforma') {
                                        cargarReformasConIA();
                                    } else if (activeTab === 'rentabilidad' && !investmentIndicatorsIA) {
                                        fetchIAAdvancedIndicators();
                                    }
                                } else {
                                    restaurarReformasOriginales();
                                }
                            }}
                        />
                        <div className="w-12 h-7 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 relative">
                            <span className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full transition-transform duration-300 ease-in-out peer-checked:translate-x-5"></span>
                        </div>
                    </label>
                </div>
            )}
        </div>
    );
};

export default PropertyAnalysis; 